import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import Button from '@/components/common/Button'
import ConfirmDialog from '@/components/common/ConfirmDialog'

interface ComposerToolbarProps {
  s: ReturnType<typeof useScenarios>
}

// シナリオ編集・新規作成モードのフローティングツールバー（マップ上部中央）。
// シナリオ名の入力とキャンセル / 登録（更新）をマップ上にまとめて置く。
export default function ComposerToolbar({ s }: ComposerToolbarProps) {
  const { t } = useTranslation()
  const isEdit = s.viewMode === 'edit'
  const saveLabel = isEdit ? t('scenarios.update') : t('scenarios.register')

  return (
    <>
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-white/95 rounded-lg shadow px-2 py-1.5">
        <span className="text-xs font-semibold text-gray-700 px-1 whitespace-nowrap">
          {isEdit ? t('scenarios.editTitle') : t('scenarios.newTitle')}
        </span>
        <input
          type="text"
          value={s.scenarioTitle}
          onChange={(e) => s.setScenarioTitle(e.target.value)}
          placeholder={t('scenarios.titlePlaceholder')}
          maxLength={20}
          className="w-44 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
        />
        <div className="w-px h-5 bg-gray-200" />
        <Button
          onClick={s.handleEditorCancel}
          variant="white"
          size="sm"
          className="whitespace-nowrap"
        >
          {t('scenarios.cancel')}
        </Button>
        <Button
          onClick={s.handleRequestSave}
          disabled={s.isCreateDisabled}
          variant="blue"
          size="sm"
          className="font-bold whitespace-nowrap"
        >
          {s.isSaving ? t('scenarios.saving') : saveLabel}
        </Button>
      </div>

      {/* ダイアログ。transform を持つツールバー div の中に置くと
          fixed の基準がずれるため、兄弟要素として描画する */}
      <ConfirmDialog
        isOpen={s.showDeleteConfirm && s.deleteTargetIndex !== null}
        title={t('scenarios.routeDeleteTitle')}
        message={t('scenarios.routeDeleteConfirm', {
          index: s.deleteTargetIndex !== null ? s.deleteTargetIndex + 1 : '',
        })}
        isAlert={true}
        onConfirm={s.handleConfirmRouteDelete}
        onCancel={s.handleCancelDelete}
      />
      <ConfirmDialog
        isOpen={s.showEditorCancelConfirm}
        title={t('scenarios.discardTitle')}
        message={t('scenarios.discardMessage')}
        confirmLabel={t('scenarios.discard')}
        isAlert={true}
        onConfirm={s.handleConfirmEditorCancel}
        onCancel={s.handleDismissEditorCancel}
      />
      <ConfirmDialog
        isOpen={s.showSaveConfirm}
        title={
          isEdit ? t('scenarios.updateTitle') : t('scenarios.registerTitle')
        }
        message={
          <>
            <p>
              {isEdit
                ? t('scenarios.updateConfirm')
                : t('scenarios.registerConfirm')}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {t('scenarios.deviceUpdateNote')}
            </p>
          </>
        }
        confirmLabel={s.isSaving ? t('scenarios.saving') : saveLabel}
        confirmDisabled={s.isSaving}
        onConfirm={s.handleConfirmSave}
        onCancel={s.handleCancelSave}
      />
    </>
  )
}
